import React, { useState, forwardRef, useImperativeHandle } from 'react';

import {
  Menu,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  ListItemButton,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';

function MenuItemWithIcon({ item }) {
  return (
    <Box
      display="flex"
      sx={{ mt: 1, mb: 0.5, minHeight: 28 }}
      alignItems="center"
      gap={0.4}>
      <item.Icon style={{ color: 'grey', flexShrink: 0 }} />
      <Typography color="primary" variant="subtitle2" component="div" noWrap>
        {item.name}
      </Typography>
    </Box>
  );
}

const SetupMenu = forwardRef(({ items, onClose, closeSetupMenu }, ref) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const visibleItems = items
    .filter(item => item.hasPermission)
    .map(item => ({
      ...item,
      children: [...item.children].sort((a, b) => a.name.localeCompare(b.name)),
    }));

  const columns = Math.max(1, visibleItems.length);
  const menuWidth = Math.min(columns * 220, 880);

  useImperativeHandle(ref, () => ({
    openMenu: anchor => setAnchorEl(anchor),
    closeMenu: () => setAnchorEl(null),
  }));

  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  const handleMenuClose = () => {
    closeSetupMenu();
    setAnchorEl(null);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '70vh',
            p: 0,
          },
        },
      }}>
      <Box
        sx={{
          width: menuWidth,
          p: 1,
          overflowY: 'auto',
          maxHeight: '70vh',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
            Setup Menu
          </Typography>
          <IconButton
            aria-label="Close setup menu"
            size="small"
            onClick={handleMenuClose}
            sx={{ ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Grid2 container spacing={3} sx={{ mt: 1 }}>
          {visibleItems.map(item => (
            <Grid2 xs={12} sm={12 / columns} md={12 / columns} key={item.name}>
              <MenuItemWithIcon item={item} />
              <Divider color="primary" />
              <Box>
                {item.children && item.children.length > 0 && (
                  <List dense>
                    {item.children.map(child => {
                      if (!child.hasPermission) {
                        return null;
                      }
                      return (
                        <ListItem dense disablePadding key={child.name}>
                          <ListItemButton
                            alignItems="flex-start"
                            disableGutters
                            dense
                            sx={{ p: 0.3 }}
                            onClick={() => {
                              child.onClick();
                              handleClose();
                            }}>
                            <ListItemText primary={child.name} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Box>
    </Menu>
  );
});

export default SetupMenu;
